import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { tryParseInt } from 'fym-common/src/utils/tryParseInt';
import { ValueOf } from 'fym-common/src/types/utilityTypes';
import { RawWidgetData, SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';
import {
  useCreateWidgetMutation,
  useDeleteWidgetMutation,
  useGetSmartLinkQuery,
  useUpdateWidgetMutation,
} from '../../../../../redux/api/fymAPI';
import { useQueryParam } from '../../../../../utils/hooks/useQueryParam';
import { getWidgetManager } from '../../widgetManagers';
import { isSuccessfulResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { ConfirmDialog } from '../../../../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogIconMark } from '../../../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { errorAlertMessage } from '../../../../common/views/ErrorAlert/errorAlertMessage';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRootParamList, SMLRouteNames, SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { TypedMemo } from '../../../../common/types';
import { presaveSubscribeMessages } from '../presaveSubscribeWidget/presaveSubscribeMessages';

interface SocialLinksEditionViewProps {
  route?: { params: SMLRootParamList[SMLRouteNames.widgetEdit] };
}

const WidgetEditionViewComponent: React.FC<SocialLinksEditionViewProps> = ({ route }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const accountIdString = useQueryParam('accountId', route);
  const accountId = tryParseInt(accountIdString);
  const smartLinkIdString = useQueryParam('smartLinkId', route);
  const smartLinkId = tryParseInt(smartLinkIdString);
  const widgetType = useQueryParam('widgetType', route) as SmartLinkWidgetType;
  const widgetUUID = useQueryParam('widgetUUID', route, null);

  if (accountId === undefined || smartLinkId === undefined || widgetType === undefined) {
    throw new Error(
      `Missing accountId: [${accountId}], smartLinkId: [${smartLinkId}] or widgetType: [${widgetType}] in SmartLinkEditionView`
    );
  }

  const [isDeletionModalOpen, setDeletionModalOpen] = useState<boolean>(false);
  const [deletionCallback, setDeletionCallback] = useState<(() => void) | undefined>(undefined);
  const { widget, isGetWidgetLoading, getWidgetError } = useGetSmartLinkQuery(
    { accountId, smartLinkId },
    {
      selectFromResult: ({ data, isLoading, error }) => ({
        widget:
          widgetUUID && data?.smartLink?.recentSmartLinkVersion?.widgets
            ? data.smartLink.recentSmartLinkVersion.widgets.find(({ uuid }) => uuid === widgetUUID)
            : undefined,
        isGetWidgetLoading: isLoading,
        getWidgetError: error,
      }),
    }
  );

  const widgetManager = useMemo(() => getWidgetManager(widgetType), [widgetType]);
  const [createWidget, { isLoading: isCreateWidgetLoading, error: createWidgetError }] = useCreateWidgetMutation();
  const [updateWidget, { isLoading: isUpdateWidgetLoading, error: updateWidgetError }] = useUpdateWidgetMutation();
  const [deleteWidget, { isLoading: isDeleteWidgetLoading, error: deleteWidgetError, reset: resetDeleteWidget }] =
    useDeleteWidgetMutation();
  // A failed delete shows in the delete dialog (design dialogs a5), not above the editor.
  const error = getWidgetError ?? updateWidgetError ?? createWidgetError;
  const isLoading = isGetWidgetLoading || isUpdateWidgetLoading || isCreateWidgetLoading || isDeleteWidgetLoading;

  const closeDeletionModal = useCallback(() => {
    setDeletionModalOpen(false);
    resetDeleteWidget();
  }, [resetDeleteWidget]);

  const saveWidget = useCallback(
    async (data: ValueOf<RawWidgetData>, type: SmartLinkWidgetType) => {
      const commonProps = {
        accountId,
        smartLinkId,
        type,
        data,
      };
      if (widget) {
        const updateWidgetResult = await updateWidget({
          ...commonProps,
          widgetUUID: widget.uuid,
          position: widget.position,
        });
        if (!isSuccessfulResult(updateWidgetResult)) {
          return;
        }
      } else {
        const createWidgetResult = await createWidget(commonProps);
        if (!isSuccessfulResult(createWidgetResult)) {
          return;
        }
      }
      router.goBack(SMLRoutes.details, { accountId, smartLinkId });
    },
    [updateWidget, router, createWidget, widget, accountId, smartLinkId]
  );

  const handleDeleteWidget = useCallback(() => {
    if (!widgetUUID) {
      return;
    }
    // The dialog stays open with a spinner until the delete ends (design dialogs a4).
    deleteWidget({ accountId, smartLinkId, widgetUUID })
      .then((result) => {
        if (!isSuccessfulResult(result)) {
          return;
        }
        setDeletionModalOpen(false);
        if (deletionCallback) {
          deletionCallback();
        }
        router.goBack(SMLRoutes.details, { accountId, smartLinkId });
      })
      .catch(console.error);
  }, [deleteWidget, accountId, smartLinkId, widgetUUID, router, deletionCallback]);

  // Deleting a pre-save block is not like deleting any other one: the server
  // takes the campaign and every sign-up with it. The artist is told before the
  // click, not after.
  // A "Get every release" block goes alone: the subscribers belong to the artist, not to the page.
  const deletionDescription = useMemo(() => {
    if (widgetType === SmartLinkWidgetType.presave) {
      return intl.formatMessage({
        id: 'app.sml.presave.block.delete_warning',
        defaultMessage:
          'This also deletes the pre-save campaign and every fan who signed up for it. It cannot be undone.',
      });
    }
    if (widgetType === SmartLinkWidgetType.presaveSubscribe) {
      return intl.formatMessage(presaveSubscribeMessages.deleteWarning);
    }
    return undefined;
  }, [intl, widgetType]);

  const triggerDeletion = useCallback(
    (callback?: () => void) => {
      if (callback) {
        setDeletionCallback(() => callback);
      }
      resetDeleteWidget();
      setDeletionModalOpen(true);
    },
    [resetDeleteWidget]
  );

  if (widgetManager === undefined) {
    // A block without an editor (the pre-save block lives on the manage
    // screen) has nothing to render here — leave instead of crashing.
    router.goBack(SMLRoutes.details, { accountId, smartLinkId });
    return null;
  }

  const EditionView = widgetManager.editionView;
  return (
    <>
      {widget && (
        <ConfirmDialog
          isOpen={isDeletionModalOpen}
          tone="danger"
          mark={<ConfirmDialogIconMark tone="danger" icon={faTrash} />}
          title={intl.formatMessage({ id: 'app.sml.widget.delete.title', defaultMessage: 'Delete this block?' })}
          description={deletionDescription}
          confirmLabel={intl.formatMessage({ id: 'app.sml.widget.delete.confirm', defaultMessage: 'Delete' })}
          isBusy={isDeleteWidgetLoading}
          error={deleteWidgetError === undefined ? undefined : errorAlertMessage(intl, deleteWidgetError)}
          onConfirm={handleDeleteWidget}
          onCancel={closeDeletionModal}
          testId="widget-delete-dialog"
        />
      )}
      <EditionView
        accountId={accountId}
        smartLinkId={smartLinkId}
        saveWidget={saveWidget}
        deleteWidget={triggerDeletion}
        isLoading={isLoading}
        error={error}
        widget={widget}
      />
    </>
  );
};

export const WidgetEditionView = TypedMemo(WidgetEditionViewComponent);
